<script lang="ts">
import type { HoverCardRootProps, PopoverContentProps, PopoverRootEmits, PopoverRootProps } from 'reka-ui'

export interface PopoverProps extends PopoverRootProps, Pick<HoverCardRootProps, 'openDelay' | 'closeDelay'> {
  mode?: 'click' | 'hover'
  portal?: boolean | string | HTMLElement
  isDismissible?: boolean
  /** When false, opening leaves focus where it was so a trigger input can stay selected. */
  openAutoFocus?: boolean
  class?: string | object
}

export interface PopoverEmits extends PopoverRootEmits {
  closePrevent: []
}

export interface PopoverSlots {
  default?: (props: { open: boolean }) => any
  content?: (props?: object) => any
  anchor?: (props?: object) => any
}

const ui = {
  content: 'p-2 bg-white border border-secondary-400 shadow-lg rounded-lg focus:outline-none pointer-events-auto',
}
</script>

<script setup lang="ts">
import { reactivePick } from '@vueuse/core'
import { useForwardPropsEmits } from 'reka-ui'
import { HoverCard, Popover } from 'reka-ui/namespaced'
import { computed, toRef } from 'vue'
import { usePortal } from '../composables/usePortal'

const props = withDefaults(defineProps<PopoverProps>(), {
  portal: true,
  mode: 'click',
  openDelay: 0,
  closeDelay: 0,
  isDismissible: true,
  openAutoFocus: true,
})
const emits = defineEmits<PopoverEmits>()
const slots = defineSlots<PopoverSlots>()

const pick = props.mode === 'hover' ? reactivePick(props, 'defaultOpen', 'open', 'openDelay', 'closeDelay') : reactivePick(props, 'defaultOpen', 'open', 'modal')
const rootProps = useForwardPropsEmits(pick, emits)
const portalProps = usePortal(toRef(() => props.portal))
const contentProps = toRef(() => ({ side: 'bottom', sideOffset: 4, collisionPadding: 4 }) as PopoverContentProps)
const contentEvents = computed(() => {
  const events: Record<string, (event: Event) => void> = {}
  if (!props.openAutoFocus) {
    events.openAutoFocus = (event: Event) => {
      event.preventDefault()
    }
  }
  if (!props.isDismissible) {
    for (const eventName of ['interactOutside', 'escapeKeyDown']) {
      events[eventName] = (event: Event) => {
        event.preventDefault()
        emits('closePrevent')
      }
    }
  }
  return events
})
const Component = computed(() => props.mode === 'hover' ? HoverCard : Popover)
</script>

<template>
  <Component.Root v-slot="{ open }" v-bind="rootProps">
    <Component.Trigger v-if="!!slots.default" as-child>
      <slot :open />
    </Component.Trigger>

    <Component.Anchor v-if="'Anchor' in Component && !!slots.anchor" as-child>
      <slot name="anchor" />
    </Component.Anchor>

    <Component.Portal v-bind="portalProps">
      <Component.Content v-if="open" v-bind="contentProps" :class="[ui.content, props.class]" v-on="contentEvents">
        <slot name="content" />
      </Component.Content>
    </Component.Portal>
  </Component.Root>
</template>
